import { IFlowDestinationListItem } from '_/features/flowDestinations/flowDestinations.types';
import { IFlowDestination, IFlowSegment, IFlowSegmentSettings } from '_/features/flows/common/types/flowSegments.types';
import { ISettings } from '_/types/settings.types';
import zeusSdk from '_/utils/SDKs';

export const getFlowSegment = async (flowSegmentId: string, params = {}): Promise<IFlowSegment> => {
  return (await zeusSdk.flowSegments.get(flowSegmentId, params)) as IFlowSegment;
};

const getExistingFlowSegmentSettings = async (flowSegmentId: string): Promise<ISettings<IFlowSegmentSettings> | undefined> => {
  try {
    return (await zeusSdk.flowSegments.getSettings(flowSegmentId)) as ISettings<IFlowSegmentSettings>;
  } catch (error: any) {
    if (error?.status === 404) {
      return undefined;
    }
    throw error;
  }
};

export const createFlowSegmentSettings = async (
  flowSegmentId: string,
  data: Partial<IFlowSegmentSettings> = {},
): Promise<ISettings<IFlowSegmentSettings>> => {
  // All settings sections share one row per segment, so it may already exist (another section
  // was unlinked first, or an earlier attempt left one behind). Check first instead of relying
  // on a 409 from the create: the global SDK error handler shows an error toast for every 409,
  // even when we recover from it. Merge into the existing row so the other sections' custom
  // settings survive (the update replaces `data` wholesale).
  const existing = await getExistingFlowSegmentSettings(flowSegmentId);
  if (existing) {
    return updateFlowSegmentSettings(flowSegmentId, { ...existing.data, ...data });
  }

  return (await zeusSdk.flowSegments.addSettings(flowSegmentId, {
    data: data,
  })) as ISettings<IFlowSegmentSettings>;
};

export const deleteFlowSegmentSettings = async (flowSegmentId: string): Promise<void> => {
  await zeusSdk.flowSegments.deleteSettings(flowSegmentId);
};

export const updateFlowSegmentSettings = async (
  flowSegmentId: string,
  data: Partial<IFlowSegmentSettings>,
): Promise<ISettings<IFlowSegmentSettings>> => {
  return (await zeusSdk.flowSegments.updateSettings(flowSegmentId, {
    data: data,
  })) as ISettings<IFlowSegmentSettings>;
};

export const getDestinations = async (flowSegmentId: string, params = {}): Promise<IFlowDestination[]> => {
  const { items } = await zeusSdk.flowSegments.getChildren(flowSegmentId, 'destinations', params);
  return items as IFlowDestination[];
};

export const getDestinationsBySegmentsIds = async (segmentsIds: string[], params = {}): Promise<IFlowDestinationListItem[]> => {
  const promises = segmentsIds.map((id) =>
    getDestinations(id, params).then((destinations) => ({ id: id, destinations: destinations })),
  );
  return Promise.all(promises);
};

export const updateOverrideDestinations = async (
  id: string,
  overrideDestinations: boolean,
  params = {},
): Promise<IFlowSegment> => {
  return (await zeusSdk.flowSegments.patch(id, { overrideDestinations: overrideDestinations }, params)) as IFlowSegment;
};

export const linkDestination = async (flowSegmentId: string, destinationId: string, params = {}): Promise<IFlowDestination> => {
  return zeusSdk.flowSegments.linkChild(flowSegmentId, 'destinations', destinationId, params);
};

export const unlinkDestination = async (
  flowSegmentId: string,
  flowDestinationId: string,
  params = {},
): Promise<IFlowDestination> => {
  return zeusSdk.flowSegments.unlinkChild(flowSegmentId, 'destinations', flowDestinationId, params);
};
